import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Link2, Unlink, Building, Check, Plus } from "lucide-react";
import { toast } from "sonner";
import InfoTooltip from "@/components/admin/InfoTooltip";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * OrgLinkedDashboardsPanel - inline mini-CRUD inside the admin Organizations panel.
 * Lists every event currently linked to this org, plus an inline link/unlink control + an "Attach existing event" search.
 */
export default function OrgLinkedDashboardsPanel({ orgId, orgName }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showAttach, setShowAttach] = useState(false);

  const auth = useCallback(() => ({ Authorization: `Bearer ${localStorage.getItem("session_token")}` }), []);

  const fetchEvents = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const r = await fetch(`${API}/admin/organizations/${orgId}/events`, { headers: auth() });
      if (r.ok) {
        const d = await r.json();
        setEvents(d.events || []);
      }
    } finally { setLoading(false); }
  }, [orgId, auth]);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  const unlink = async (eventId) => {
    const r = await fetch(`${API}/admin/organizations/${orgId}/unlink-event/${eventId}`, { method: "POST", headers: auth() });
    if (r.ok) { toast.success("Unlinked"); fetchEvents(); } else { toast.error("Unlink failed"); }
  };

  const linkEvent = async (eventId) => {
    const r = await fetch(`${API}/admin/organizations/${orgId}/link-event/${eventId}`, { method: "POST", headers: auth() });
    if (r.ok) {
      toast.success(`Linked to ${orgName}`);
      setSearch(""); setSearchResults([]); setShowAttach(false);
      fetchEvents();
    } else { toast.error("Link failed"); }
  };

  // Search events by name (uses the existing admin event search if available; falls back to admin/events list)
  const runSearch = async (q) => {
    if (!q.trim()) { setSearchResults([]); return; }
    setSearching(true);
    try {
      const r = await fetch(`${API}/admin/events?search=${encodeURIComponent(q)}`, { headers: auth() });
      if (r.ok) {
        const d = await r.json();
        setSearchResults((d.events || []).filter(e => e.org_id !== orgId).slice(0, 8));
      }
    } finally { setSearching(false); }
  };

  if (!orgId) return null;

  return (
    <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-lg" data-testid="org-linked-dashboards-panel">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-xs font-semibold text-gray-700 flex items-center gap-1">
          <Building className="w-3.5 h-3.5" /> Linked host dashboards ({events.length})
          <InfoTooltip
            label="Linked host dashboards"
            description="Every event whose organization_id matches this org. Use Attach existing to swap an event's organization_id without recreating the dashboard. Unlink keeps the data but archives the host_access_assignment."
            source="db.events.organization_id"
            where_synced={["Host dashboard owner column", "Org member counts", "Brand one-pager attribution"]}
          />
        </h4>
        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setShowAttach(!showAttach)} data-testid="attach-event-toggle">
          <Plus className="w-3 h-3 mr-1" /> Attach existing
        </Button>
      </div>

      {loading ? (
        <div className="py-3 flex justify-center"><Loader2 className="w-4 h-4 animate-spin text-gray-400" /></div>
      ) : events.length === 0 ? (
        <p className="text-xs text-gray-400 py-2">No host dashboards linked yet.</p>
      ) : (
        <div className="space-y-1">
          {events.map(e => (
            <div key={e.event_id} className="flex items-center gap-2 px-2 py-1.5 rounded bg-white border border-gray-100 text-xs" data-testid={`linked-event-${e.event_id}`}>
              <Link2 className="w-3 h-3 text-gray-400 flex-shrink-0" />
              <span className="font-medium text-gray-800 truncate flex-1">{e.name || e.event_id}</span>
              <span className="text-gray-400">{e.dashboard_status || "-"}</span>
              <button onClick={() => unlink(e.event_id)} className="text-red-500 hover:text-red-700 ml-1" data-testid={`unlink-event-${e.event_id}`}>
                <Unlink className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {showAttach && (
        <div className="mt-3 pt-3 border-t border-gray-200">
          <Input
            placeholder="Search events to attach..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); runSearch(e.target.value); }}
            className="text-xs h-8"
            data-testid="attach-event-search"
          />
          {searching && <div className="py-2 flex justify-center"><Loader2 className="w-4 h-4 animate-spin text-gray-400" /></div>}
          {searchResults.length > 0 && (
            <div className="mt-2 space-y-1 max-h-40 overflow-y-auto" data-testid="attach-event-results">
              {searchResults.map(e => (
                <button
                  key={e.event_id}
                  onClick={() => linkEvent(e.event_id)}
                  className="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-100 text-left text-xs"
                  data-testid={`link-event-${e.event_id}`}
                >
                  <Check className="w-3 h-3 text-green-600" />
                  <span className="font-medium text-gray-800 truncate flex-1">{e.name || e.event_id}</span>
                  {e.org_id && <span className="text-gray-400 text-[10px]">currently: {e.organization_name || e.org_id}</span>}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
